import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import CookieConsent from "@/components/CookieConsent";
import Landing from "@/pages/Landing";
const Aujourdhui = lazy(() => import("@/pages/Aujourdhui"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Account = lazy(() => import("@/pages/Account"));
const LessonView = lazy(() => import("@/pages/LessonView"));
const Library = lazy(() => import("@/pages/Library"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Admin = lazy(() => import("@/pages/Admin"));
const CustomStories = lazy(() => import("@/pages/CustomStories"));
const Premium = lazy(() => import("@/pages/Premium"));
const Verify = lazy(() => import("@/pages/Verify"));
const JoinByCode = lazy(() => import("@/pages/JoinByCode"));
const Legal = lazy(() => import("@/pages/Legal"));

// « / » renvoie les connectés vers leur page du jour, sauf « /?accueil=1 » : le lien « Accueil du
// site » de l'en-tête. nginx sert la même page pré-rendue pour « / » avec ou sans paramètre.
const HomeGate = () => {
    const { status } = useAuth();
    const [params] = useSearchParams();
    if (status === "loading") {
        return (
            <div
                data-testid="home-loading"
                className="cj min-h-screen flex items-center justify-center text-ink/60 text-xl"
            >
                Une minute, on tisse l&apos;histoire…
            </div>
        );
    }
    if (status === "authed" && params.get("accueil") !== "1") return <Navigate to="/aujourdhui" replace />;
    return <Landing />;
};

const AppShell = () => (
    <div className="min-h-screen flex flex-col bg-parchment text-ink">
        <Header />
        <EmailVerifyBanner />
        <main className="flex-1">
            <ErrorBoundary>
                {/* Pages privées et atelier chargés à la demande (React.lazy) : l'accueil
                    ne télécharge plus l'atelier d'administration. */}
                <Suspense
                    fallback={
                        <div className="container-edit py-24 font-serif italic text-ink/60 text-xl" role="status">
                            Chargement…
                        </div>
                    }
                >
                <Routes>
                    <Route path="/" element={<HomeGate />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verifier-email" element={<Verify />} />
                    <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
                    <Route path="/reinitialiser" element={<ResetPassword />} />
                    <Route path="/rejoindre" element={<JoinByCode />} />
                    <Route path="/bienvenue" element={<Welcome />} />
                    <Route
                        path="/aujourdhui"
                        element={
                            <ProtectedRoute>
                                <Aujourdhui />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/bibliotheque"
                        element={
                            <ProtectedRoute>
                                <Library />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lecon/:id"
                        element={
                            <ProtectedRoute>
                                <LessonView mode="detail" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/compte"
                        element={
                            <ProtectedRoute>
                                <Account />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sur-mesure"
                        element={
                            <ProtectedRoute>
                                <CustomStories />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/premium"
                        element={
                            <ProtectedRoute>
                                <Premium />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/*"
                        element={
                            <ProtectedRoute>
                                <Admin />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/mentions-legales" element={<Legal doc="mentions" />} />
                    <Route path="/confidentialite" element={<Legal doc="confidentialite" />} />
                    <Route path="/cgu" element={<Legal doc="cgu" />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
            </ErrorBoundary>
        </main>
        <Footer />
        <CookieConsent />
    </div>
);

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppShell />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
